import GlobalStyles from '@mui/joy/GlobalStyles';
import Box from '@mui/joy/Box';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FactoryIcon from '@mui/icons-material/Factory';
import './Sidebar.scss';
import { Accordian } from '@consolidocs-ui/components';
import { useNavigate, useLocation } from 'react-router';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import Logo from '_assets/consolidocs-text.png';
import subLogo from '_assets/logo-purple.png';
import SidebarFooter from './SidebarFooter';

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ open = false, onClose }: SidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <>
      <Sheet
        className="Sidebar"
        sx={{
          position: { xs: 'fixed', md: 'sticky' },
          transform: {
            xs: open ? 'translateX(0)' : 'translateX(-100%)',
            md: 'none',
          },
          transition: 'transform 0.4s, width 0.4s',
          zIndex: 10000,
          height: '100vh',
          // No overflow here: the nav scrolls on its own, and the version
          // panel in the footer must be free to reach past the sidebar edge.
          width: 'var(--Sidebar-width)',
          top: 0,
          p: 0,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            ':root': {
              '--Sidebar-width': '220px',
              [theme.breakpoints.up('lg')]: {
                '--Sidebar-width': '240px',
              },
            },
          })}
        />

        {/* Brand header — light background with the wordmark inverted to
          dark so it stays legible on the sidebar surface. */}
        <Box
          className="sidebar-brand"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 48,
            px: 2,
            bgcolor: 'background.surface',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <img
            src={subLogo}
            alt=""
            aria-hidden
            style={{
              height: 24,
              objectFit: 'contain',
              flexShrink: 0,
              filter: 'invert(1)',
            }}
          />
          <img
            src={Logo}
            alt="ConsolidatedDocs"
            style={{
              height: 20,
              objectFit: 'contain',
              minWidth: 0,
              filter: 'invert(1)',
            }}
          />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            overflow: 'hidden auto',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            [`& .${listItemButtonClasses.root}`]: {
              gap: 1.5,
            },
          }}
        >
          <List
            size="sm"
            sx={{
              gap: 1,
              '--List-nestedInsetStart': '30px',
              '--ListItem-radius': (theme) => theme.vars.radius.sm,
            }}
          >
            <ListItem>
              <ListItemButton
                selected={pathname === '/'}
                onClick={() => navigate('/')}
              >
                <HomeRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Companies</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>

            {/* Global Settings menu item - commented out */}
            <ListItem>
              <ListItemButton
                selected={pathname === '/global-settings'}
                onClick={() => navigate('/global-settings')}
              >
                <SettingsIcon />
                <ListItemContent>
                  <Typography level="title-sm">Global Settings</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>

            <ListItem nested>
              <Accordian
                defaultExpanded={true}
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <FactoryIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Industries</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={open ? { transform: 'rotate(180deg)' } : {}}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                  <ListItemButton
                    selected={pathname === '/industries'}
                    onClick={() => navigate('/industries')}
                  >
                    <ListItemContent>
                      <Typography level="title-sm"> All Industries</Typography>
                    </ListItemContent>
                  </ListItemButton>
                  <ListItemButton
                    selected={pathname === '/industries/create'}
                    onClick={() => navigate('/industries/create')}
                  >
                    <ListItemContent>
                      <Typography level="title-sm"> Create Industry</Typography>
                    </ListItemContent>
                  </ListItemButton>
                </List>
              </Accordian>
            </ListItem>

            <ListItem nested>
              <Accordian
                defaultExpanded={true}
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <HomeRoundedIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Contracts</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={open ? { transform: 'rotate(180deg)' } : {}}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                  <ListItemButton
                    selected={pathname === '/contracts'}
                    onClick={() => navigate('/contracts')}
                  >
                    <ListItemContent>
                      <Typography level="title-sm"> All Contracts</Typography>
                    </ListItemContent>
                  </ListItemButton>
                  <ListItemButton
                    selected={pathname === '/contracts/create'}
                    onClick={() => navigate('/contracts/create')}
                  >
                    <ListItemContent>
                      <Typography level="title-sm"> Create Contract</Typography>
                    </ListItemContent>
                  </ListItemButton>
                </List>
              </Accordian>
            </ListItem>
          </List>
        </Box>

        <SidebarFooter />
      </Sheet>
      <Box
        className={`Sidebar-overlay${open ? ' open' : ''}`}
        sx={{ display: { md: 'none' } }}
        onClick={onClose}
      />
    </>
  );
}
